/**
 * resolveCart() -- the single source of truth for what a customer sees and what they pay.
 *
 * Before this existed the pricing rules lived in FIVE places: computeTotals(),
 * getProductsFromQuestions(), and the order-summary JSX (all three client-side), plus the
 * Express and Cloudflare handlers. The client copies decided the displayed price; the
 * server copies decided the charged price; nothing reconciled them.
 *
 * The key design decision is that `lineItems` and the lookup-key arrays are derived from
 * ONE traversal. The order summary renders lineItems; the checkout request sends the lookup
 * keys from that same array. Display and payload cannot diverge, by construction.
 *
 * Pure and side-effect free, so the client can useMemo it (it used to be recomputed 8-9
 * times per render) and the server can call it with no Stripe round trip.
 */

import {
  primaryProducts,
  addonSubscriptions,
  oneTimeProducts,
  setupFeeConfig,
  questions,
  ITEM_KIND,
} from './catalog.js';
import { isItemVisible } from './visibility.js';
import { resolveFeatures } from './flags.js';

/**
 * Normalize a selection map that may contain explicit `false` values.
 * handleAddonToggle writes `false` rather than deleting the key, so Object.keys() includes
 * deselected ids. Filter on truthiness once, here, instead of at every call site.
 */
function selectedIds(map) {
  if (!map) return [];
  if (Array.isArray(map)) return map.filter(Boolean);
  return Object.keys(map).filter((id) => map[id]);
}

/**
 * Normalize a question answer to an array of selected answer ids.
 *
 * This is what lets radio and checkbox share one code path. The four near-duplicate
 * branches in the original (two in computeTotals, two in getProductsFromQuestions)
 * differed only in this normalization step.
 *
 * Answers are polymorphic: a string for radio, an object for checkbox. localStorage can
 * rehydrate either shape from an older build, and an array has been observed too, so all
 * three are handled defensively.
 */
function answerIdsFor(question, rawAnswer) {
  if (!rawAnswer) return [];
  if (typeof rawAnswer === 'string') return [rawAnswer];
  if (Array.isArray(rawAnswer)) return rawAnswer.filter(Boolean);
  if (typeof rawAnswer === 'object') {
    return Object.keys(rawAnswer).filter((id) => rawAnswer[id]);
  }
  return [];
}

/** Resolve the priced product attached to one answer, if any. */
function productForAnswer(answer, primaryPlanId) {
  const addProduct = answer?.addProduct;
  if (!addProduct) return null;

  const { type, conditionalPricing, fixedProduct } = addProduct;

  // conditionalPricing is keyed by primary plan id -- this is what makes e.g. SEO content
  // $999 on starter and $1,998 on business, with a different lookup key for each.
  if (conditionalPricing && conditionalPricing[primaryPlanId]) {
    const priceInfo = conditionalPricing[primaryPlanId];
    if (!priceInfo?.lookup_key) return null;
    return {
      lookup_key: priceInfo.lookup_key,
      price_display: priceInfo.price_display || 0,
      isSubscription: type === 'subscription',
    };
  }

  if (fixedProduct?.lookup_key) {
    return {
      lookup_key: fixedProduct.lookup_key,
      price_display: fixedProduct.price_display || 0,
      isSubscription: type === 'subscription',
    };
  }

  return null;
}

/**
 * @typedef {object} Selection
 * @property {?string} primaryPlanId  e.g. 'business-plan'
 * @property {string}  term           'monthly' | 'yearly'
 * @property {object|string[]} addonIds
 * @property {object|string[]} oneTimeIds
 * @property {object}  questionAnswers  { [questionId]: string | {answerId: bool} }
 * @property {?object} coupon         null | {type:'no_setup_fee'} | {type:'stripe', percent_off?, amount_off?}
 */

/**
 * @param {Selection} selection
 * @param {{features?: object}} [opts]
 * @returns {{lineItems: object[], subscriptionLookupKeys: string[],
 *   oneTimeLookupKeys: string[], totals: object, term: string,
 *   primaryPlanId: ?string, warnings: string[]}}
 */
export function resolveCart(selection, opts = {}) {
  const term = selection?.term || 'monthly';
  const primaryPlanId = selection?.primaryPlanId || null;
  const coupon = selection?.coupon || null;
  const features = resolveFeatures(opts.features);
  const ctx = { term, primaryPlanId };

  const lineItems = [];
  const warnings = [];

  // --- Primary plan -------------------------------------------------------------
  if (primaryPlanId) {
    const plan = primaryProducts.find((p) => p.id === primaryPlanId);
    if (!plan) {
      warnings.push(`Unknown primary plan '${primaryPlanId}'`);
    } else {
      const termObj = (plan.terms || []).find((t) => t.id === term);
      if (!termObj) {
        warnings.push(`Plan '${primaryPlanId}' has no '${term}' term`);
      } else {
        lineItems.push({
          key: `primary:${plan.id}`,
          label: plan.name,
          sublabel: term === 'yearly' ? 'Yearly subscription' : 'Monthly subscription',
          kind: ITEM_KIND.PRIMARY,
          lookup_key: termObj.lookup_key,
          price_display: termObj.price_display || 0,
          isRecurring: true,
        });
      }
    }
  }

  // --- Add-on subscriptions -----------------------------------------------------
  // The flag gates PRICING, not just UI. A stale localStorage blob or a shared URL can
  // carry addon ids; without this check they would be charged while invisible on screen.
  if (features.addonSubscriptions) {
    selectedIds(selection?.addonIds).forEach((addonId) => {
      const addon = addonSubscriptions.find((a) => a.id === addonId);
      if (!addon) {
        warnings.push(`Unknown addon '${addonId}'`);
        return;
      }
      if (!isItemVisible(addon, ctx)) {
        // Previously computeTotals priced this while the key collectors dropped it, so the
        // displayed total included an addon that never reached Stripe.
        warnings.push(`Dropped addon '${addonId}': not available for ${primaryPlanId}/${term}`);
        return;
      }
      const termObj = (addon.terms || []).find((t) => t.id === term);
      if (!termObj) {
        warnings.push(`Addon '${addonId}' has no '${term}' term`);
        return;
      }
      lineItems.push({
        key: `addon:${addon.id}`,
        label: addon.name,
        sublabel: term === 'yearly' ? 'Yearly add-on' : 'Monthly add-on',
        kind: ITEM_KIND.ADDON,
        lookup_key: termObj.lookup_key,
        price_display: termObj.price_display || 0,
        isRecurring: true,
      });
    });
  }

  // --- One-time products --------------------------------------------------------
  if (features.oneTimeServices) {
    selectedIds(selection?.oneTimeIds).forEach((productId) => {
      const product = oneTimeProducts.find((p) => p.id === productId);
      if (!product) {
        warnings.push(`Unknown one-time product '${productId}'`);
        return;
      }
      if (!isItemVisible(product, ctx)) {
        warnings.push(`Dropped one-time '${productId}': not available for ${primaryPlanId}/${term}`);
        return;
      }
      if (!product.lookup_key) return;
      lineItems.push({
        key: `one-time:${product.id}`,
        label: product.name,
        sublabel: 'One-time',
        kind: ITEM_KIND.ONE_TIME,
        lookup_key: product.lookup_key,
        price_display: product.price_display || 0,
        isRecurring: false,
      });
    });
  }

  // --- Question-driven products -------------------------------------------------
  // Requires a primary plan: conditionalPricing is keyed by plan id, so without one there
  // is no price to resolve. Matches the original guards.
  if (primaryPlanId) {
    questions.forEach((question) => {
      answerIdsFor(question, selection?.questionAnswers?.[question.id]).forEach((answerId) => {
        const answer = (question.answers || []).find((a) => a.id === answerId);
        if (!answer) return;

        const product = productForAnswer(answer, primaryPlanId);
        if (!product) return; // informational answer, adds nothing

        lineItems.push({
          key: `question:${question.id}:${answer.id}`,
          label: answer.label || question.title,
          sublabel: product.isSubscription ? 'Subscription' : 'One-time',
          kind: ITEM_KIND.QUESTION,
          lookup_key: product.lookup_key,
          price_display: product.price_display,
          isRecurring: Boolean(product.isSubscription),
        });
      });
    });
  }

  // --- Setup fee ----------------------------------------------------------------
  // Derived from the resolved cart, never from a client-supplied billing_term. Requires a
  // real recurring plan in the cart, so an addon-only or one-time-only cart pays none.
  const hasRecurring = lineItems.some((li) => li.isRecurring);
  let setupFee = 0;

  if (
    setupFeeConfig?.enabled &&
    term === 'monthly' &&
    primaryPlanId &&
    hasRecurring &&
    coupon?.type !== 'no_setup_fee'
  ) {
    setupFee = setupFeeConfig.monthlySetupFee.price_display || 0;
    lineItems.push({
      key: 'setup-fee',
      label: 'Setup fee',
      sublabel: 'One-time, monthly plans only',
      kind: ITEM_KIND.SETUP_FEE,
      lookup_key: setupFeeConfig.monthlySetupFee.lookup_key,
      price_display: setupFee,
      isRecurring: false,
    });
  }

  // --- Totals -------------------------------------------------------------------
  // subtotal excludes the setup fee so the summary can show it as its own row, matching
  // the previous behavior.
  const subtotal = lineItems
    .filter((li) => li.kind !== ITEM_KIND.SETUP_FEE)
    .reduce((sum, li) => sum + (li.price_display || 0), 0);

  const subtotalWithSetupFee = subtotal + setupFee;

  let discountAmount = 0;
  if (coupon?.type === 'stripe') {
    if (coupon.percent_off) {
      // Applied to subtotal + setup fee, preserving the original behavior.
      discountAmount = (subtotalWithSetupFee * coupon.percent_off) / 100;
    } else if (coupon.amount_off) {
      discountAmount = coupon.amount_off / 100; // Stripe amount_off is in cents
    }
  }

  // Never let a discount produce a negative total.
  discountAmount = Math.min(discountAmount, subtotalWithSetupFee);
  const total = subtotalWithSetupFee - discountAmount;

  /*
    What recurs after today, for the summary's "then $X monthly" line.

    ADDED FOR THE REDESIGN, and deliberately here rather than in the component that displays it.
    The order summary needs to distinguish "total due today" (which includes one-time services
    and the setup fee) from "what you pay every period after that". Summing it in OrderSummary
    would put an amount-producing calculation in a component, and CLAUDE.md is unambiguous that
    money logic lives in this directory and nowhere else -- that rule is why five disagreeing
    copies of the pricing rules were consolidated in the first place.

    Not discounted: a Stripe coupon may be one-time or recurring and this module is not told
    which, so quoting a reduced ongoing figure could understate what the customer is charged
    next month. The undiscounted amount is the honest one to show.
  */
  const recurringTotal = lineItems
    .filter((li) => li.isRecurring)
    .reduce((sum, li) => sum + (li.price_display || 0), 0);

  // Lookup keys come from the very array the customer saw priced.
  const subscriptionLookupKeys = lineItems
    .filter((li) => li.isRecurring)
    .map((li) => li.lookup_key)
    .filter(Boolean);

  const oneTimeLookupKeys = lineItems
    .filter((li) => !li.isRecurring)
    .map((li) => li.lookup_key)
    .filter(Boolean);

  return {
    lineItems,
    subscriptionLookupKeys,
    oneTimeLookupKeys,
    totals: { subtotal, setupFee, discountAmount, total, recurringTotal },
    term,
    primaryPlanId,
    warnings,
  };
}
